import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { AppShell } from './components/layout/AppShell';
import { AuthGuard } from './auth/guards/AuthGuard';
import { useAuth } from './auth/AuthContext';
import { routes } from './routes';

function LoadingFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

function LoginPage() {
  const { login, isLoading } = useAuth();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        bgcolor: 'background.default',
        gap: 3,
      }}
    >
      <Box
        component="img"
        src="/vite.svg"
        alt="VT Logo"
        sx={{ width: 80, height: 80 }}
      />
      <Box sx={{ typography: 'h4', fontWeight: 600, color: 'text.primary' }}>
        VT Employee Dashboard
      </Box>
      <Box sx={{ typography: 'body1', color: 'text.secondary', mb: 2 }}>
        Sign in to access the dashboard
      </Box>
      <Box
        component="button"
        onClick={login}
        disabled={isLoading}
        sx={{
          px: 4,
          py: 1.5,
          bgcolor: 'primary.main',
          color: 'white',
          border: 'none',
          borderRadius: 2,
          fontSize: '1rem',
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          '&:disabled': {
            opacity: 0.7,
            cursor: 'not-allowed',
          },
        }}
      >
        {isLoading ? 'Signing in...' : 'Sign In with VT'}
      </Box>
    </Box>
  );
}

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route
          path="/*"
          element={
            <AuthGuard>
              <AppShell>
                <Routes>
                  {routes.map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route.element}
                    />
                  ))}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppShell>
            </AuthGuard>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;
